import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useProductService } from 'services/products';
import { useProductsData } from 'hooks/useProductsData';
import { Product, CreateProductRequest } from 'types/product';
import { ROUTES } from 'utils/constants';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import {
  Plus,
  Pencil,
  Search,
  Package,
  Send,
  Archive,
  Copy,
  Loader2,
} from 'lucide-react';
import styles from './Products.module.scss';

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

function Products() {
  const navigate = useNavigate();
  const productService = useProductService();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [productToPublish, setProductToPublish] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [productToArchive, setProductToArchive] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [productToDuplicate, setProductToDuplicate] = useState<{
    id: string;
    name: string;
    product: Product;
  } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Use products data hook
  const { products, loading, reloading, error, reload, updateProducts } =
    useProductsData({ showArchived });

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) {
      return products;
    }
    const query = searchQuery.toLowerCase();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query),
    );
  }, [products, searchQuery]);

  // Paginate filtered products
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredProducts.length / pageSize);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleProductClick = useCallback(
    (productId: string) => {
      navigate(ROUTES.PRODUCTS.EDIT.replace(':id', productId));
    },
    [navigate],
  );

  const handlePublishClick = useCallback(
    (e: React.MouseEvent, productId: string, productName: string) => {
      e.stopPropagation();
      setProductToPublish({ id: productId, name: productName });
      setShowPublishModal(true);
    },
    [],
  );

  const handlePublishConfirm = useCallback(async () => {
    if (!productToPublish) return;

    setIsPublishing(true);
    try {
      const publishedProduct = await productService.publishDraftProduct(
        productToPublish.id,
      );
      // Update the product in the list
      const updatedProducts = products.map((p) =>
        p.id === publishedProduct.id ? publishedProduct : p,
      );
      updateProducts(updatedProducts);
      showToast('Draft published successfully!', 'success');
      setShowPublishModal(false);
      setProductToPublish(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to publish draft. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsPublishing(false);
    }
  }, [productToPublish, productService, showToast, products, updateProducts]);

  const handlePublishCancel = useCallback(() => {
    setShowPublishModal(false);
    setProductToPublish(null);
  }, []);

  const handleEditFromModal = useCallback(() => {
    if (productToPublish) {
      setShowPublishModal(false);
      navigate(ROUTES.PRODUCTS.EDIT.replace(':id', productToPublish.id));
    }
  }, [productToPublish, navigate]);

  const handleArchiveClick = useCallback(
    (e: React.MouseEvent, productId: string, productName: string) => {
      e.stopPropagation();
      setProductToArchive({ id: productId, name: productName });
      setArchiveReason('');
      setShowArchiveModal(true);
    },
    [],
  );

  const handleArchiveConfirm = useCallback(async () => {
    if (!productToArchive) return;

    setIsArchiving(true);
    try {
      await productService.deleteProduct(
        productToArchive.id,
        archiveReason.trim() || undefined,
      );
      // Manually update the product to mark it as archived
      const updatedProducts = products.map((p) =>
        p.id === productToArchive.id
          ? {
              ...p,
              isVoided: true,
              voidedAt: new Date().toISOString(),
              voidReason: archiveReason.trim() || null,
            }
          : p,
      );
      updateProducts(updatedProducts);
      showToast('Product archived successfully!', 'success');
      setShowArchiveModal(false);
      setProductToArchive(null);
      setArchiveReason('');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to archive product. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsArchiving(false);
    }
  }, [
    productToArchive,
    archiveReason,
    productService,
    showToast,
    products,
    updateProducts,
  ]);

  const handleArchiveCancel = useCallback(() => {
    setShowArchiveModal(false);
    setProductToArchive(null);
    setArchiveReason('');
  }, []);

  const handleDuplicateClick = useCallback(
    (e: React.MouseEvent, product: Product) => {
      e.stopPropagation();
      setProductToDuplicate({
        id: product.id,
        name: product.name,
        product,
      });
      setShowDuplicateModal(true);
    },
    [],
  );

  const handleDuplicateConfirm = useCallback(async () => {
    if (!productToDuplicate) return;

    setIsDuplicating(true);
    try {
      const product = productToDuplicate.product;
      // Create duplicate product as draft
      const duplicateData: CreateProductRequest = {
        name: `${product.name} (Copy)`,
        description: product.description,
        media: product.media,
        productCategory: product.productCategory,
        price: Number(product.price),
        trackInventory: product.trackInventory,
        isShippable: product.isShippable,
        shippingDetails: product.shippingDetails,
      };

      const duplicatedProduct =
        await productService.saveDraftProduct(duplicateData);
      // Add the new product to the list
      const updatedProducts = [duplicatedProduct, ...products];
      updateProducts(updatedProducts);
      showToast('Product duplicated successfully!', 'success');
      setShowDuplicateModal(false);
      setProductToDuplicate(null);
      // Navigate to edit page for the new draft
      setTimeout(() => {
        navigate(ROUTES.PRODUCTS.EDIT.replace(':id', duplicatedProduct.id));
      }, 1000);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to duplicate product. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsDuplicating(false);
    }
  }, [
    productToDuplicate,
    productService,
    showToast,
    products,
    updateProducts,
    navigate,
  ]);

  const handleDuplicateCancel = useCallback(() => {
    setShowDuplicateModal(false);
    setProductToDuplicate(null);
  }, []);

  const handleCreateProduct = useCallback(() => {
    navigate(ROUTES.PRODUCTS.ADD);
  }, [navigate]);

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className={styles.header}>
        <h1 className={styles.title}>Products</h1>
        <div className={styles.headerButtons}>
          <RefreshButton onClick={reload} loading={reloading} />
          <Button
            onClick={handleCreateProduct}
            className={styles.createButton}
            aria-label="Create new product"
          >
            <Plus size={18} />
            Create Product
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading products...</div>
      ) : (
        <div className={reloading ? styles.contentReloading : ''}>
          {error && !loading && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <Button onClick={reload}>Retry</Button>
            </div>
          )}

          {!error && (
            <>
              <div className={styles.searchContainer}>
                <div className={styles.searchInputWrapper}>
                  <Search size={18} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search products by name or description..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className={styles.searchInput}
                  />
                </div>
                <div className={styles.filterContainer}>
                  <label className={styles.filterLabel}>
                    <input
                      type="checkbox"
                      checked={showArchived}
                      onChange={(e) => {
                        setShowArchived(e.target.checked);
                        setCurrentPage(1);
                      }}
                      className={styles.filterCheckbox}
                    />
                    Show archived products
                  </label>
                </div>
              </div>

              {filteredProducts.length === 0 ? (
                <div className={styles.emptyState}>
                  <Package size={48} className={styles.emptyIcon} />
                  <p>
                    {searchQuery
                      ? 'No products found matching your search.'
                      : 'No products yet. Create your first product to get started.'}
                  </p>
                  {!searchQuery && (
                    <Button onClick={handleCreateProduct}>
                      Create Product
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className={styles.productsTable}>
                    <div className={styles.tableHeader}>
                      <div className={styles.tableHeaderCell}>Name</div>
                      <div className={styles.tableHeaderCell}>Description</div>
                      <div className={styles.tableHeaderCell}>Category</div>
                      <div className={styles.tableHeaderCell}>Price</div>
                      <div className={styles.tableHeaderCell}>Type</div>
                      <div className={styles.tableHeaderCell}>Stock</div>
                      <div className={styles.tableHeaderCell} aria-hidden />
                    </div>
                    <div className={styles.tableBody}>
                      {paginatedProducts.map((product) => (
                        <div
                          key={product.id}
                          className={styles.tableRow}
                          onClick={() => handleProductClick(product.id)}
                        >
                          <div className={styles.tableCell} data-label="Name">
                            <div className={styles.productNameContainer}>
                              <span className={styles.productName}>
                                {product.name}
                              </span>
                              {product.isDraft && (
                                <span className={styles.draftBadge}>Draft</span>
                              )}
                              {product.isVoided && (
                                <span className={styles.archivedBadge}>
                                  Archived
                                </span>
                              )}
                            </div>
                          </div>
                          <div
                            className={styles.tableCell}
                            data-label="Description"
                          >
                            <span className={styles.productDescription}>
                              {product.description || '-'}
                            </span>
                          </div>
                          <div
                            className={styles.tableCell}
                            data-label="Category"
                          >
                            <span>{product.productCategory || '-'}</span>
                          </div>
                          <div className={styles.tableCell} data-label="Price">
                            <span className={styles.price}>
                              {formatCurrency(Number(product.price))}
                            </span>
                          </div>
                          <div className={styles.tableCell} data-label="Type">
                            <span
                              className={`${styles.typeBadge} ${
                                !product.trackInventory
                                  ? styles.serviceBadge
                                  : styles.productBadge
                              }`}
                            >
                              {!product.trackInventory ? 'Service' : 'Product'}
                            </span>
                          </div>
                          <div className={styles.tableCell} data-label="Stock">
                            <span>
                              {product.trackInventory && product.inventory
                                ? Number(product.inventory.quantity)
                                : '-'}
                            </span>
                          </div>
                          <div
                            className={styles.tableCell}
                            data-label="Actions"
                          >
                            <div className={styles.actionButtons}>
                              {product.isDraft && !product.isVoided && (
                                <button
                                  className={styles.publishButton}
                                  onClick={(e) => {
                                    handlePublishClick(
                                      e,
                                      product.id,
                                      product.name,
                                    );
                                  }}
                                  aria-label={`Publish ${product.name}`}
                                  title="Publish draft"
                                >
                                  <Send size={16} />
                                </button>
                              )}
                              {!product.isVoided && (
                                <button
                                  className={styles.editButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProductClick(product.id);
                                  }}
                                  aria-label={`Edit ${product.name}`}
                                >
                                  <Pencil size={16} />
                                </button>
                              )}
                              {!product.isVoided && (
                                <button
                                  className={styles.archiveButton}
                                  onClick={(e) => {
                                    handleArchiveClick(
                                      e,
                                      product.id,
                                      product.name,
                                    );
                                  }}
                                  aria-label={`Archive ${product.name}`}
                                  title="Archive product"
                                >
                                  <Archive size={16} />
                                </button>
                              )}
                              {product.isVoided && (
                                <button
                                  className={styles.duplicateButton}
                                  onClick={(e) => {
                                    handleDuplicateClick(e, product);
                                  }}
                                  aria-label={`Duplicate ${product.name}`}
                                  title="Duplicate product"
                                >
                                  <Copy size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalItems={filteredProducts.length}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* Publish Confirmation Modal */}
          <Modal
            show={showPublishModal}
            onHide={handlePublishCancel}
            centered
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>Publish Draft Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Are you sure you want to publish{' '}
                <strong>"{productToPublish?.name}"</strong>?
              </p>
              <p className={styles.modalWarning}>
                This will make the product active and available for use in
                transactions. Make sure all required fields are filled before
                publishing.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handlePublishCancel}
                disabled={isPublishing}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleEditFromModal}
                disabled={isPublishing}
              >
                <Pencil size={18} style={{ marginRight: '0.5rem' }} />
                Edit Product
              </Button>
              <Button
                variant="primary"
                onClick={() => void handlePublishConfirm()}
                disabled={isPublishing}
              >
                {isPublishing ? (
                  <Loader2
                    size={18}
                    className={styles.spinningIcon}
                    style={{ marginRight: '0.5rem' }}
                  />
                ) : (
                  <Send size={18} style={{ marginRight: '0.5rem' }} />
                )}
                {isPublishing ? 'Publishing...' : 'Publish Product'}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Archive Confirmation Modal */}
          <Modal
            show={showArchiveModal}
            onHide={handleArchiveCancel}
            centered
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>Archive Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Are you sure you want to archive{' '}
                <strong>"{productToArchive?.name}"</strong>?
              </p>
              <p className={styles.modalWarning}>
                This will archive the product. It will no longer be available
                for use in transactions, but can be restored later.
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="archiveReason" className={styles.label}>
                  Reason (optional)
                </label>
                <textarea
                  id="archiveReason"
                  placeholder="Enter the reason for archiving this product..."
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  className={styles.textareaInput}
                  rows={3}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleArchiveCancel}
                disabled={isArchiving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleArchiveConfirm()}
                disabled={isArchiving}
              >
                {isArchiving ? (
                  <Loader2
                    size={18}
                    className={styles.spinningIcon}
                    style={{ marginRight: '0.5rem' }}
                  />
                ) : (
                  <Archive size={18} style={{ marginRight: '0.5rem' }} />
                )}
                {isArchiving ? 'Archiving...' : 'Archive Product'}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Duplicate Confirmation Modal */}
          <Modal
            show={showDuplicateModal}
            onHide={handleDuplicateCancel}
            centered
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>Duplicate Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Are you sure you want to duplicate{' '}
                <strong>"{productToDuplicate?.name}"</strong>?
              </p>
              <p className={styles.modalWarning}>
                This will create a new draft product with the same information.
                You can edit and publish it later.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleDuplicateCancel}
                disabled={isDuplicating}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  void handleDuplicateConfirm();
                }}
                disabled={isDuplicating}
              >
                {isDuplicating ? (
                  <Loader2
                    size={18}
                    className={styles.spinningIcon}
                    style={{ marginRight: '0.5rem' }}
                  />
                ) : (
                  <Copy size={18} style={{ marginRight: '0.5rem' }} />
                )}
                {isDuplicating ? 'Duplicating...' : 'Duplicate Product'}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </div>
  );
}

export default Products;
